import React, { useState, useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import { Blog } from "../../services/dashboard/GetBlogs";
import { editBlog } from "../../services/dashboard/EditBlog";
import LoadingSpinner from "../utility/LoadingSpinner";
import CustomDropdown from "../app/CustomDropdown"; // Importar CustomDropdown
import "../../styles/dash/CreateOrEditChatBot.css"; // Reusing styles for consistency

interface Props {
    blog: Blog | null;
    show: boolean;
    onClose: () => void;
    onSuccess: (updatedBlog: Blog) => void;
}

const EditBlogModal: React.FC<Props> = ({ blog, show, onClose, onSuccess }) => {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [imageBase64, setImageBase64] = useState("");
    const [seo, setSeo] = useState("");
    const [overrideLanguage, setOverrideLanguage] = useState<string | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [animateOut, setAnimateOut] = useState(false);

    const URL_BACK_IMG = process.env.REACT_APP_API_IMGBLOG_URL;

    const languageOptions = [
        { value: "es", label: "Español" },
        { value: "en", label: "Inglés" },
    ];

    // Siempre deriva el estado inicial del prop para evitar problemas de sincronización
    useEffect(() => {
        if (blog) {
            setTitle(blog.title);
            setDescription(blog.description);
            setImageBase64(blog.imageName);
            setImagePreview(`${URL_BACK_IMG}${blog.imageName}`);
            setSeo(blog.seo);
            setOverrideLanguage(null); // Resetea en cambio de blog
            setErrorMessage("");
        }
    }, [blog]);

    const handleClose = () => {
        setAnimateOut(true);
        setTimeout(() => {
            onClose();
            setAnimateOut(false);
        }, 300);
    };

    const handleSubmit = async () => {
        if (!blog) return;

        if (!title.trim() || !description.trim() || !imageBase64.trim() || !seo.trim()) {
            setErrorMessage("Todos los campos, incluida la imagen, son obligatorios.");
            return;
        }

        setLoading(true);
        setErrorMessage("");

        const finalLanguage = overrideLanguage || blog.language;

        const updatedBlogData: Blog = {
            ...blog,
            title,
            description,
            imageName: imageBase64,
            seo,
            language: finalLanguage.toUpperCase(),
        };

        try {
            console.log(updatedBlogData,"updatedBlogData")
            
            const response = await editBlog(updatedBlogData);
            if (response.status === 200) {
                onSuccess(updatedBlogData);
                handleClose();
            } else {
                setErrorMessage(response.message);
            }
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : "Ocurrió un error inesperado.");
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                setErrorMessage("La imagen no puede superar los 5MB.");
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result as string;
                setImageBase64(base64String);
                setImagePreview(base64String);
                setErrorMessage("");
            };
            reader.onerror = () => {
                setErrorMessage("Error al leer la imagen.");
            };
            reader.readAsDataURL(file);
        }
    };

    if (!show && !animateOut) return null;

    // Deriva el valor a mostrar en el dropdown directamente
    const displayLanguage = overrideLanguage || (blog?.language || 'es').toLowerCase();

    return (
        <Modal
            show={!animateOut}
            onHide={handleClose}
            centered
            size="lg"
            backdrop="static"
            contentClassName={`chatbot-form-modal ${animateOut ? 'modal-exit' : 'modal-enter'}`}
        >
            <Modal.Header closeButton closeVariant="white" className="border-0" />
            <Modal.Body className="chatbot-form-content">
                <h5 className="form-title text-center mb-4">Editar Blog</h5>
                <Form>
                    {loading && <LoadingSpinner />}
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label>Título</label>
                            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título del blog" />
                        </div>
                        <div className="form-group full-width">
                            <label>Descripción</label>
                            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Descripción..." rows={5}></textarea>
                        </div>
                        <div className="form-group">
                            <label>Imagen de Portada</label>
                            <input
                                type="file"
                                id="imageUploadEdit"
                                accept="image/png, image/jpeg, image/webp"
                                onChange={handleImageChange}
                                style={{ display: 'none' }}
                            />
                            <label htmlFor="imageUploadEdit" className="file-upload-button">
                                {imagePreview ? "Cambiar Imagen" : "Seleccionar Imagen"}
                            </label>
                            {imagePreview && (
                                <img src={imagePreview} alt="Vista previa" className="image-preview" />
                            )}
                        </div>
                        <div className="form-group full-width">
                            <label>Idioma</label>
                            <CustomDropdown
                                key={blog?.id} // Forzar re-montaje cuando el blog cambia
                                options={languageOptions}
                                defaultValue={displayLanguage}
                                onChange={setOverrideLanguage}
                            />
                        </div>
                        <div className="form-group">
                            <label>SEO</label>
                            <input type="text" value={seo} onChange={(e) => setSeo(e.target.value)} placeholder="Palabras clave, etc." />
                        </div>
                        <div className="form-group full-width">
                            {errorMessage && <div className="error-message">{errorMessage}</div>}
                            <button type="button" className="save-button" onClick={handleSubmit} disabled={loading}>
                                {loading ? "GUARDANDO..." : "GUARDAR CAMBIOS"}
                            </button>
                        </div>
                        <div className="form-group full-width text-center">
                            <button type="button" className="cancel-button" onClick={handleClose} disabled={loading}>
                                Cerrar
                            </button>
                        </div>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default EditBlogModal;
